import { useFieldArray, UseFormReturn } from 'react-hook-form';
import { ColorInterval, MiscellaneousField } from '../../reportsTypes';
import {
  Button,
  ColorPicker,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  InfoIconTooltip,
  Input,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@ais-ui/ui-core';
import React from 'react';
import { PiMinusThin, PiPlusThin } from 'react-icons/pi';
import { useLocalization } from '@ais-ui/locale-management';

interface ColorIntervalItemProps {
  fieldName: string;
  form: UseFormReturn<FormValues>;
  fieldInfo: MiscellaneousField;
}
interface FormValues {
  [key: string]: ColorInterval[];
}

export const MiscellaneousColorIntervalItem = ({
  fieldName,
  form,
  fieldInfo,
}: ColorIntervalItemProps) => {
  const { messages } = useLocalization();
  const { fields, append, remove } = useFieldArray<ColorInterval>({
    control: form.control as any,
    name: fieldName as never,
  }) as unknown as {
    fields: ColorInterval[];
    append: (...args: any[]) => any;
    remove: (...args: any[]) => any;
  }; // Cannot get those type to match up

  return (
    <FormField
      key={fieldName}
      control={form.control}
      name={fieldName}
      render={({ field }) => (
        <FormItem>
          <div className="flex justify-between">
            <FormLabel>
              {fieldInfo.name}: {fieldInfo.required && '*'}
            </FormLabel>
            {fieldInfo.help && (
              <InfoIconTooltip>{fieldInfo.help}</InfoIconTooltip>
            )}
          </div>
          <FormControl onChange={() => form.clearErrors()}>
            <>
              {fields.map((colorInterval: ColorInterval, index) => (
                <div key={colorInterval.id} className="flex space-x-2">
                  <ColorPicker
                    background={colorInterval.color}
                    setBackground={(color) => {
                      const fieldNamePath = `${fieldName}[${index}].color`;
                      form.setValue(fieldNamePath, color as any);
                      fields[index].color = color;
                    }}
                    className="flex-shrink-0 flex-grow-0 w-12 p-0"
                  />

                  <Input
                    type="number"
                    defaultValue={colorInterval.startInterval}
                    onChange={(e) => {
                      if (!e.target.value) {
                        // Handle empty input, e.g., set it to undefined
                        form.setValue(
                          `${fieldName}[${index}].startInterval`,
                          0 as never,
                        );
                      } else {
                        // Convert the string value to a number
                        const numberValue = parseFloat(e.target.value);
                        form.setValue(
                          `${fieldName}[${index}].startInterval`,
                          numberValue as never,
                        );
                      }
                    }}
                  />
                  <Input
                    type="number"
                    defaultValue={colorInterval.endInterval}
                    onChange={(e) => {
                      if (!e.target.value) {
                        // Handle empty input, e.g., set it to undefined
                        form.setValue(
                          `${fieldName}[${index}].endInterval`,
                          0 as never,
                        );
                      } else {
                        // Convert the string value to a number
                        const numberValue = parseFloat(e.target.value);
                        form.setValue(
                          `${fieldName}[${index}].endInterval`,
                          numberValue as never,
                        );
                      }
                    }}
                  />
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        type="button"
                        size="icon"
                        variant="outline"
                        className="h-9 w-9 flex-shrink-0 flex-grow-0"
                        onClick={() => remove(index)}
                      >
                        <PiMinusThin className="size-4"></PiMinusThin>
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      {
                        messages.reportsCreateReportMiscellaneousColorIntervalItemRemove
                      }
                    </TooltipContent>
                  </Tooltip>
                </div>
              ))}

              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    type="button"
                    size="icon"
                    variant="outline"
                    className="h-9 w-9"
                    onClick={() =>
                      append({
                        color: '##003b5c',
                        startInterval: 0,
                        endInterval: 0,
                      })
                    }
                  >
                    <PiPlusThin className="size-4"></PiPlusThin>
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  {
                    messages.reportsCreateReportMiscellaneousColorIntervalItemAdd
                  }
                </TooltipContent>
              </Tooltip>
            </>
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  );
};
